<template>
  <div class="cwg" :class="{ 'cwg--mobile': isMobileLayout }">
    <div class="cwg-toolbar">
      <span v-if="data && data.truncated" class="cwg-toolbar__state">{{ $tn(`${kindCfg.i18n}.truncated`) }}</span>
      <span v-if="saveState === 'error'" class="cwg-toolbar__state text-danger">{{ $tn("dashboardWidgets.saveError") }}</span>
      <button
        v-if="isMobileLayout && visibleWidgets.length > 1"
        type="button"
        class="btn btn-sm"
        :class="reordering ? 'btn-primary' : 'btn-outline-secondary'"
        @click="reordering = !reordering"
      >
        <i class="fas" :class="reordering ? 'fa-check' : 'fa-sort'" aria-hidden="true"></i>
        {{ reordering ? $tn("dashboardWidgets.doneReordering") : $tn("dashboardWidgets.reorder") }}
      </button>
      <button type="button" class="btn btn-sm btn-outline-primary" @click="catalogOpen = true">
        <i class="fas fa-th-large" aria-hidden="true"></i>
        {{ $tn("dashboardWidgets.manage") }}
      </button>
    </div>

    <p v-if="layoutReady && !visibleWidgets.length" class="cwg-empty">
      {{ $tn(`${kindCfg.i18n}.empty`) }}
      <button type="button" class="btn btn-sm btn-primary ms-2" @click="catalogOpen = true">
        <i class="fas fa-plus" aria-hidden="true"></i> {{ $tn("dashboardWidgets.catalog.add") }}
      </button>
    </p>

    <!-- Escritorio: misma grilla que el dashboard (se arrastra desde el encabezado; al
         tomar un widget la vista se achica y al soltarlo se adapta al ancho del hueco). -->
    <div
      v-if="!isMobileLayout && layoutReady"
      ref="canvas"
      class="cwg-canvas"
      :class="{ 'is-zoomed': dragScale < 1 }"
      :style="canvasStyle"
      @pointerdown.capture="maybeZoomOut"
    >
    <grid-layout
      ref="grid"
      :transform-scale="dragScale"
      :layout="visibleWidgets"
      :col-num="cols"
      :row-height="rowHeight"
      :margin="[margin, margin]"
      :is-draggable="true"
      :is-resizable="true"
      :vertical-compact="true"
      :use-css-transforms="false"
      :responsive="false"
    >
      <grid-item
        v-for="w in visibleWidgets"
        :key="w.i"
        :i="w.i"
        :x="w.x"
        :y="w.y"
        :w="w.w"
        :h="w.h"
        :min-w="entryOf(w).min.w"
        :min-h="entryOf(w).min.h"
        drag-allow-from=".dw-head"
        :drag-option="dragOption"
        drag-ignore-from="button, a, input, textarea, select, .dw-actions"
        :class="{ 'cwg-item--menu-open': openMenuId === w.i }"
        @moved="scheduleSave"
        @resized="scheduleSave"
      >
        <DashboardWidgetFrame
          :title="entryOf(w).title"
          :icon="entryOf(w).icon"
          :menu-items="menus[w.i] || []"
          draggable
          @menu="onMenu(w, $event)"
          @menu-open="openMenuId = $event ? w.i : null"
          @remove="removeWidget(w)"
        >
          <component
            :is="kindCfg.widget"
            :ref="`widget-${w.i}`"
            :entry="entryOf(w)"
            :item-id="itemId"
            :config="w.config || {}"
            :data="data"
            :loading="loading"
            :error="error"
            @menu="setMenu(w, $event)"
            @update-config="updateConfig(w, $event)"
          />
        </DashboardWidgetFrame>
      </grid-item>
    </grid-layout>
    </div>

    <!-- Teléfono: una columna en el orden del layout. -->
    <div v-else-if="isMobileLayout && layoutReady" class="cwg-stack">
      <DashboardWidgetFrame
        v-for="(w, idx) in mobileOrder"
        :key="w.i"
        mobile
        class="dw--capped"
        :title="entryOf(w).title"
        :icon="entryOf(w).icon"
        :menu-items="menus[w.i] || []"
        :reordering="reordering"
        :can-up="idx > 0"
        :can-down="idx < mobileOrder.length - 1"
        @menu="onMenu(w, $event)"
        @remove="removeWidget(w)"
        @up="moveMobile(idx, -1)"
        @down="moveMobile(idx, 1)"
      >
        <component
          :is="kindCfg.widget"
          :ref="`widget-${w.i}`"
          :entry="entryOf(w)"
            :item-id="itemId"
          :config="w.config || {}"
          :data="data"
          :loading="loading"
          :error="error"
          @menu="setMenu(w, $event)"
          @update-config="updateConfig(w, $event)"
        />
      </DashboardWidgetFrame>
    </div>

    <DashboardWidgetCatalog
      v-model="catalogOpen"
      :entries="catalog"
      :added-types="addedTypes"
      :loading="loading && !data"
      :title="$tn(`${kindCfg.i18n}.catalogTitle`)"
      :subtitle="$tn(`${kindCfg.i18n}.catalogSubtitle`)"
      @add="addWidget"
      @remove="removeByType"
      @reset="resetLayout"
    />
  </div>
</template>

<script>
/**
 * Pestaña «Insights» de la ficha de un contacto o de un producto: widgets como los del
 * dashboard sobre las operaciones en que aparece. Cada usuario elige los suyos (los
 * mismos para todos los contactos, y otros para todos los productos; se guardan por
 * `kind`). Los datos se piden una vez por item y los comparten todos los widgets; el
 * de mercado del producto pide los suyos solo si está en la pestaña.
 */
import { GridLayout, GridItem } from "vue-grid-layout";
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import Vue from "vue";
import mobileLayoutMixin from "@/mixins/mobileLayoutMixin";
import gridDragMixin from "@/components/gotrading/dashboard/gridDragMixin";
import "@/components/gotrading/dashboard/widgetTokens.css";
import DashboardWidgetFrame from "@/components/gotrading/dashboard/DashboardWidgetFrame.vue";
import DashboardWidgetCatalog from "@/components/gotrading/dashboard/DashboardWidgetCatalog.vue";
import { GRID_COLS, ROW_HEIGHT, GRID_MARGIN, placeNewWidget } from "@/components/gotrading/dashboard/widgetCatalog";
import ContactInsightWidget from "./ContactInsightWidget.vue";
import ProductInsightWidget from "./ProductInsightWidget.vue";
import { buildContactCatalog, buildDefaultContactLayout } from "./contactWidgetCatalog";
import { buildProductCatalog, buildDefaultProductLayout } from "./productWidgetCatalog";

const LAYOUT_URL = "/api/users/contact-widgets-layout";

/** Lo que cambia entre contactos y productos. */
const KINDS = {
  contact: {
    dataUrl: (id) => `/api/contact-insights/${id}`,
    i18n: "contactWidgets",
    widget: ContactInsightWidget,
    buildCatalog: buildContactCatalog,
    buildDefault: buildDefaultContactLayout,
  },
  product: {
    dataUrl: (id) => `/api/product-insights/${id}`,
    i18n: "productWidgets",
    widget: ProductInsightWidget,
    buildCatalog: buildProductCatalog,
    buildDefault: buildDefaultProductLayout,
  },
};

export default {
  name: "InsightWidgetGrid",
  components: { GridLayout, GridItem, DashboardWidgetFrame, DashboardWidgetCatalog },
  mixins: [mobileLayoutMixin, gridDragMixin],
  inject: {
    dashboardLayout: { from: "dashboardLayout", default: null },
  },
  props: {
    /** «contact» o «product». */
    kind: { type: String, default: "contact" },
    itemId: { type: String, required: true },
  },
  data() {
    return {
      cols: GRID_COLS,
      rowHeight: ROW_HEIGHT,
      margin: GRID_MARGIN,
      /** null = nunca lo personalizó: se muestra el de fábrica. */
      savedWidgets: null,
      layoutReady: false,
      data: null,
      loading: false,
      error: false,
      catalogOpen: false,
      reordering: false,
      menus: {},
      openMenuId: null,
      saveState: "",
    };
  },
  computed: {
    kindCfg() {
      return KINDS[this.kind] || KINDS.contact;
    },
    catalog() {
      const ctx = {
        finance: !!(this.data && this.data.finance),
        financeModule: !!(this.dashboardLayout && this.dashboardLayout.isFinancialModuleActive),
      };
      return this.kindCfg.buildCatalog(ctx).map((e) => {
        const key = `${this.kindCfg.i18n}.items.${e.type.replace(/[:-]/g, "_")}`;
        return { ...e, title: this.$t(`${key}.title`), description: this.$tn(`${key}.description`) };
      });
    },
    catalogByType() {
      return Object.fromEntries(this.catalog.map((e) => [e.type, e]));
    },
    defaultWidgets() {
      // Observable: al arrastrar se le cambia el ancho en el lugar aunque sea el de fábrica.
      return Vue.observable(this.kindCfg.buildDefault(this.catalog));
    },
    widgets() {
      return this.savedWidgets || this.defaultWidgets;
    },
    /** Un widget de montos sin permiso queda guardado pero no se ve. */
    visibleWidgets() {
      return this.widgets.filter((w) => this.catalogByType[w.type]);
    },
    mobileOrder() {
      return [...this.visibleWidgets].sort((a, b) => a.y - b.y || a.x - b.x);
    },
    addedTypes() {
      return this.visibleWidgets.map((w) => w.type);
    },
  },
  watch: {
    itemId() {
      this.loadData();
    },
  },
  async mounted() {
    this.loadData();
    try {
      const { data } = await wencesApi.get(LAYOUT_URL, { params: { kind: this.kind, _: Date.now() } });
      const widgets = data && data.data && data.data.widgets;
      this.savedWidgets = Array.isArray(widgets) ? widgets.map((w) => ({ config: {}, ...w })) : null;
    } catch (e) {
      this.savedWidgets = null;
    } finally {
      this.layoutReady = true;
    }
  },
  beforeDestroy() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.persist();
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await wencesApi.get(this.kindCfg.dataUrl(this.itemId), { params: { _: Date.now() } });
        this.data = data.data;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    entryOf(w) {
      return this.catalogByType[w.type] || { min: { w: 2, h: 3 }, icon: "", title: "" };
    },
    setMenu(w, items) {
      this.$set(this.menus, w.i, items || []);
    },
    onMenu(w, key) {
      if (key === "refresh") {
        this.loadData();
        return;
      }
      const ref = this.$refs[`widget-${w.i}`];
      const vm = Array.isArray(ref) ? ref[0] : ref;
      if (vm && typeof vm.onMenu === "function") vm.onMenu(key);
    },
    /** Primera modificación: el de fábrica pasa a ser el layout propio del usuario. */
    materialize() {
      if (!this.savedWidgets) {
        this.savedWidgets = this.defaultWidgets.map((w) => ({ ...w, config: { ...(w.config || {}) } }));
      }
    },
    updateConfig(w, patch) {
      this.materialize();
      const target = this.savedWidgets.find((x) => x.i === w.i);
      if (!target) return;
      target.config = { ...(target.config || {}), ...patch };
      this.scheduleSave();
    },
    addWidget(entry) {
      this.materialize();
      const { w, h } = entry.size;
      const pos = this.isMobileLayout
        ? { x: 0, y: this.savedWidgets.reduce((m, it) => Math.max(m, it.y + it.h), 0) }
        : placeNewWidget(this.visibleWidgets, w);
      this.savedWidgets = this.savedWidgets.filter((x) => x.i !== entry.type);
      this.savedWidgets.push({ i: entry.type, type: entry.type, x: pos.x, y: pos.y, w, h, config: { ...(entry.config || {}) } });
      this.scheduleSave(0);
    },
    removeWidget(w) {
      this.materialize();
      this.savedWidgets = this.savedWidgets.filter((x) => x.i !== w.i);
      this.$delete(this.menus, w.i);
      this.scheduleSave(0);
    },
    removeByType(entry) {
      const w = this.visibleWidgets.find((x) => x.type === entry.type);
      if (w) this.removeWidget(w);
    },
    moveMobile(idx, dir) {
      this.materialize();
      const order = this.mobileOrder;
      const a = this.savedWidgets.find((x) => x.i === order[idx].i);
      const b = this.savedWidgets.find((x) => x.i === order[idx + dir].i);
      if (!a || !b) return;
      const { x: ax, y: ay } = a;
      a.x = b.x;
      a.y = b.y;
      b.x = ax;
      b.y = ay;
      if (a.y === b.y && a.x === b.x) b.y += dir > 0 ? -1 : 1;
      this.scheduleSave();
    },
    async resetLayout() {
      const { isConfirmed } = await Swal.fire({
        title: this.$tn("dashboardWidgets.resetConfirmTitle"),
        text: this.$tn(`${this.kindCfg.i18n}.resetConfirmText`),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$tn("dashboardWidgets.catalog.reset"),
        cancelButtonText: this.$tn("dashboardWidgets.cancel"),
      });
      if (!isConfirmed) return;
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      try {
        await wencesApi.delete(LAYOUT_URL, { params: { kind: this.kind } });
        this.savedWidgets = null;
        this.menus = {};
        this.catalogOpen = false;
      } catch (e) {
        this.saveState = "error";
      }
    },
    scheduleSave(delay = 800) {
      this.materialize();
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        this.persist();
      }, delay);
    },
    /** Los guardados van en fila: el último en salir es el último en llegar. */
    persist() {
      this.persistChain = (this.persistChain || Promise.resolve()).then(() => this.persistNow());
      return this.persistChain;
    },
    async persistNow() {
      if (!this.savedWidgets) return;
      const widgets = this.savedWidgets.map(({ i, type, x, y, w, h, config }) => ({ i, type, x, y, w, h, config: config || {} }));
      this.saveState = "saving";
      try {
        await wencesApi.put(LAYOUT_URL, { widgets }, { params: { kind: this.kind } });
        this.saveState = "";
      } catch (e) {
        this.saveState = "error";
      }
    },
  },
};
</script>

<style scoped>
.cwg { position: relative; }
.cwg-toolbar { display: flex; justify-content: flex-end; align-items: center; gap: 8px; margin: 4px 0; flex-wrap: wrap; }
.cwg-toolbar__state { font-size: 0.78rem; color: var(--dw-muted); margin-right: auto; }
.cwg-toolbar .btn { display: inline-flex; align-items: center; gap: 6px; }
.cwg-empty { text-align: center; padding: 40px 12px; color: var(--dw-muted); border: 2px dashed var(--dw-border); border-radius: 12px; margin: 8px 0; }
.cwg ::v-deep .vue-grid-layout { margin: 0 -12px; }
.cwg ::v-deep .vue-grid-item.vue-grid-placeholder { background: rgba(var(--bs-primary-rgb), 0.18); border-radius: 12px; opacity: 1; }
.cwg ::v-deep .vue-grid-item.resizing,
.cwg ::v-deep .vue-grid-item.vue-draggable-dragging { z-index: 30; opacity: 0.92; }
.cwg ::v-deep .vue-grid-item > .vue-resizable-handle { background: none; width: 16px; height: 16px; right: 3px; bottom: 3px; opacity: 0; transition: opacity 0.15s ease; z-index: 5; }
.cwg ::v-deep .vue-grid-item > .vue-resizable-handle::after {
  content: "";
  position: absolute;
  right: 3px;
  bottom: 3px;
  width: 8px;
  height: 8px;
  border-right: 2px solid var(--dw-muted);
  border-bottom: 2px solid var(--dw-muted);
  border-bottom-right-radius: 3px;
}
.cwg ::v-deep .vue-grid-item:hover > .vue-resizable-handle { opacity: 1; }
.cwg ::v-deep .vue-grid-item.cwg-item--menu-open { z-index: 25; }
/* Achicar es instantáneo (la grilla mide al empezar el arrastre); volver, suave. */
.cwg-canvas { transition: transform 0.2s ease; }
.cwg-canvas.is-zoomed { transition: none; }
.cwg-canvas.is-zoomed ::v-deep .vue-grid-item:not(.vue-draggable-dragging):not(.vue-grid-placeholder) { opacity: 0.85; }
.cwg-stack { display: flex; flex-direction: column; gap: 12px; }
</style>
